import { Device } from '@weenat/client/dist/resources/devices'
import { Plot } from '@weenat/client/dist/resources/plots'
import { Station } from '@weenat/client/dist/resources/stations'
import { useOrgContext } from 'app/orgProvider'
import { useMatch } from 'app/routx-router'
import { isEmpty, isNil } from 'lodash-es'
import DelimitedFlex from '../kit/DelimitedFlex'
import Breadcrumbs from '../kit/breadcrumbs/Breadcrumbs'
import useBreadcrumbs from '../kit/breadcrumbs/useBreadcrumbs'
import HeaderActionsArea from './HeaderActionsArea'
import ResourceNavigator from './ResourceNavigator'
import ResourceSwitcher, { TitleText } from './ResourceSwitcher'

interface MenuLayoutProps {
  resource?: Station | Device | Plot
}

const MenuLayout: FC<MenuLayoutProps> = ({ children, resource }) => {
  const breadcrumbs = useBreadcrumbs()
  const org = useOrgContext()

  const matchStations = useMatch(`/farms/*/stations/*`)
  const matchPlots = useMatch(`/farms/*/plots/*`)
  const matchArchivedPlots = useMatch(`/farms/*/plots/archived`)
  const matchWeatherMapDevice = useMatch('/weather-map/*')

  const showElementDropdown =
    !isNil(matchStations) ||
    (!isNil(matchPlots) && isNil(matchArchivedPlots)) ||
    !isNil(matchWeatherMapDevice)

  // On plot details we never want to show paths after the plot name, so we stick to that
  // For others we want the last crumb
  const titleCrumb =
    !isEmpty(breadcrumbs) && !isNil(breadcrumbs)
      ? !isNil(matchPlots) && breadcrumbs.length >= 3
        ? [...breadcrumbs][2]
        : [...breadcrumbs].pop()
      : null

  return (
    <Flex
      $position='relative'
      $flexDirection='column'
      $width='100%'
      $flex={1}
      $height={'100%'}
      $backgroundColor={'grayscale.white'}
      $pointerEvents={'auto'}
      $overflowY='auto'
    >
      {/* Crumbs */}
      {breadcrumbs.length > 1 ? (
        <DelimitedFlex $alignItems='center' $px={'lg'} $py={'md'}>
          <Breadcrumbs crumbs={breadcrumbs} />
        </DelimitedFlex>
      ) : null}

      <DelimitedFlex $height={48} $alignItems='center' $px={'lg'} $py={'md'} $gap={'md'}>
        <Box $flex={1} $minWidth={0}>
          {showElementDropdown ? (
            <ResourceSwitcher
              title={titleCrumb?.label ?? ''}
              organizationId={org.id}
              isWeatherMap={!isNil(matchWeatherMapDevice)}
              enabled={showElementDropdown}
            />
          ) : (
            <TitleText title={titleCrumb?.label ?? ''} />
          )}
        </Box>
        <HeaderActionsArea />
      </DelimitedFlex>

      {!isNil(resource) ? <ResourceNavigator resource={resource} /> : null}

      <Flex $flexDirection='column' $flex={1} $backgroundColor={'grayscale.50'}>
        {children}
      </Flex>
    </Flex>
  )
}

export default MenuLayout
